import { css, Theme } from '@emotion/react';
import {
  AccountCircle,
  Dehaze,
  EmojiEvents,
  Feed,
  Flag,
  Palette,
  Settings,
  Storefront,
  SupervisorAccount,
} from '@mui/icons-material';
import { Box } from '@mui/material';
import { useMemo } from 'react';

import { SettingsSidebar as Sidebar, SettingsSidebarItem } from 'shared/components/navigation';
import { ROUTES } from 'shared/constants';
import { useFeatureEnabled } from 'shared/hooks';

import { SettingsSidebarProfile } from './SettingsSidebarProfile';

const containerCss = (theme: Theme) => css`
  border-right: 1px solid ${theme.palette.divider};
`;

export const SettingsSidebar = () => {
  const { enabled: showRewardingSettings } = useFeatureEnabled('rewardActions');
  const { enabled: showContentRestrictionsSettings } = useFeatureEnabled('contentRestrictions');
  const { enabled: showCommunityProductsSettings } = useFeatureEnabled('communityMarketplace');

  const sidebarLinks = useMemo(() => {
    const links: SettingsSidebarItem[] = [
      {
        title: 'Site Settings',
        icon: <Settings />,
        route: ROUTES.admin.settings.siteSettings,
      },
      {
        title: 'Main Navigation',
        icon: <Dehaze />,
        route: ROUTES.admin.settings.navigation,
      },
      {
        title: 'Top Bar News Ticker',
        icon: <Feed />,
        route: ROUTES.admin.settings.news,
      },
      {
        title: 'Site Admins',
        icon: <AccountCircle />,
        route: ROUTES.admin.settings.siteAdmins,
      },
      {
        title: 'Customization',
        icon: <Palette />,
        route: ROUTES.admin.settings.customization,
      },
      {
        title: 'Roles and Permissions',
        icon: <SupervisorAccount />,
        route: ROUTES.admin.settings.roles,
      },
      {
        title: 'Content Auto-Moderation',
        icon: <Flag />,
        route: ROUTES.admin.settings.contentModeration,
      },
    ];

    if (showRewardingSettings) {
      links.push({
        title: 'Rewards Settings',
        icon: <EmojiEvents />,
        route: ROUTES.admin.settings.rewarding,
      });
    }

    if (showContentRestrictionsSettings) {
      links.push({
        title: 'Content Restrictions',
        icon: <Flag />,
        route: ROUTES.admin.settings.contentRestrictions,
      });
    }

    if (showCommunityProductsSettings) {
      links.push({
        title: 'Community Products / Memberships',
        icon: <Storefront />,
        route: ROUTES.admin.settings.communityProducts,
      });
    }

    return links;
  }, [showRewardingSettings, showContentRestrictionsSettings, showCommunityProductsSettings]);

  return (
    <Box css={containerCss}>
      <SettingsSidebarProfile />
      <Sidebar items={sidebarLinks} />
    </Box>
  );
};
